import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHighlight } from "../../context/HighlightContext";
import { useLocation } from "react-router-dom";

import "./Highlight.css";

import rangy from 'rangy';
import 'rangy/lib/rangy-classapplier';
import 'rangy/lib/rangy-highlighter';
import 'rangy/lib/rangy-serializer';


const getStorageTarget = (letter) => {
  switch (letter) {
    case "I":
      return "user";
    case "T":
      return "team";
    case "B":
      return "both";
    default:
      return "user"; // Fallback
  }
};

const getSelectionElement = (node) => {
  if (node instanceof Element) return node;
  if (node && node.parentElement instanceof Element) return node.parentElement;
  if (node && node.parentNode instanceof Element) return node.parentNode;
  return null;
};

const isDisallowedHighlightTarget = (node) => {
  const element = getSelectionElement(node);
  if (!element) return true;
  return !!element.closest(
    "button, a, input, textarea, select, option, summary, label, nav, [role='button'], [role='tab'], [contenteditable='true'], .zoom-toggle-btn, .tabs, .tab, .tab-btn, .guide-tab"
  );
};

const normalizeContainer = (container) => {
  if (container) {
    container.normalize();
  }
};


const Highlight = () => {
  const { highlights, addHighlight, removeHighlight, highlightSource } = useHighlight();
  const [showHighlightBox, setShowHighlightBox] = useState(false);
  const [highlightBoxPosition, setHighlightBoxPosition] = useState({ top: 0, left: 0 });
  const [highlighter, setHighlighter] = useState(null);
  const [selectedHighlightMeta, setSelectedHighlightMeta] = useState(null);
  const metaBoxRef = useRef(null);
  const highlightsRef = useRef([]);
  const location = useLocation();
  const currentPath = location?.pathname || window.location.pathname;





  useEffect(() => {
    rangy.init();
    const rHighlighter = rangy.createHighlighter();
  
    const colors = ["#00FF00", "#FF0000", "#FFFF00", "#FFC0CB", "#ADD8E6", "#FFA500"];
    colors.forEach((color) => {
      const className = `highlight-${color.replace("#", "")}`;
      rHighlighter.addClassApplier(
        rangy.createClassApplier(className, {
          elementTagName: "span",
          elementProperties: {
            onclick: (e) => {
              const id = e.currentTarget.getAttribute("data-highlight-id");
              const span = e.currentTarget;
              const className = Array.from(span.classList).find(cls => cls.startsWith("highlight-"));
              const container = span.closest("[data-container-id]");
              const containerId = container?.getAttribute("data-container-id") || null;
  
              const fallback = {
                id,
                className,
                color: null,
                letter: localStorage.getItem("highlightLetter") || "I",
                location: window.location.pathname,
                containerId,
                serializedRange: null,
                excerpt: span.textContent,
              };
  
              const clickedHighlight = highlightsRef.current.find(h => h.id === id) || fallback;
              const isUserSaved = highlightsRef.current.some(h => h.id === id && h.savedIn?.user);
              const isTeamSaved = highlightsRef.current.some(h => h.id === id && h.savedIn?.team);
  
              setSelectedHighlightMeta({
                ...clickedHighlight,
                savedIn: {
                  user: isUserSaved,
                  team: isTeamSaved
                },
                position: {
                  top: e.clientY + window.scrollY,
                  left: e.clientX + window.scrollX
                }
              });
            }
          }
        })
      );
    });
  
    setHighlighter(rHighlighter);
  }, []); // ✅ One-time init now works
  
  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);
  
  
  
  
  useEffect(() => {
    if (!selectedHighlightMeta) return;
  
    const handleClickOutside = (e) => {
      if (metaBoxRef.current && !metaBoxRef.current.contains(e.target)) {
        setSelectedHighlightMeta(null);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedHighlightMeta]);
  

  // State for the letter box
  const [letter, setLetter] = useState(() => localStorage.getItem("highlightLetter") || "I");

  // Map each letter to its tooltip text
  const tooltipTextMap = {
    I: "Individual: save highlight to user only",
    T: "Team: save highlight to team",
    B: "Both: save highlight to both user and team"
  };

  // Function to handle letter cycling and storing in localStorage
  const handleLetterClick = () => {
    const letterCycle = ["I", "T", "B"];
    const nextIndex = (letterCycle.indexOf(letter) + 1) % letterCycle.length;
    const nextLetter = letterCycle[nextIndex];
    setLetter(nextLetter);
    localStorage.setItem("highlightLetter", nextLetter);
  };

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.toString().trim() || selection.rangeCount === 0) {
      setShowHighlightBox(false);
      return;
    }

    const range = selection.getRangeAt(0);
    if (
      isDisallowedHighlightTarget(range.startContainer) ||
      isDisallowedHighlightTarget(range.endContainer)
    ) {
      setShowHighlightBox(false);
      return;
    }

    const startElement = getSelectionElement(range.startContainer);
    const endElement = getSelectionElement(range.endContainer);
    const container = startElement?.closest("[data-container-id]") || null;
    if (!container || !endElement || !container.contains(endElement)) {
      setShowHighlightBox(false);
      return;
    }

    const rect = range.getBoundingClientRect();
    const containerId = container.getAttribute("data-container-id");

    setHighlightBoxPosition({
      top: rect.top + window.scrollY - 40,
      left: rect.left + window.scrollX,
    });
    setShowHighlightBox(true);
    console.log("Text selected:", selection.toString(), { containerId });
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleTextSelection);
    return () => {
      document.removeEventListener("mouseup", handleTextSelection);
    };
  }, [handleTextSelection]);
  

  const handleHighlight = async (color) => {
    const selection = rangy.getSelection();
    if (!selection || selection.isCollapsed) return;
    if (!highlighter) return;
  
    const rawRange = selection.getRangeAt(0);
    const range = rawRange.cloneRange();

    if (
      isDisallowedHighlightTarget(range.startContainer) ||
      isDisallowedHighlightTarget(range.endContainer)
    ) {
      setShowHighlightBox(false);
      return;
    }

    const startElement = getSelectionElement(range.startContainer);
    const endElement = getSelectionElement(range.endContainer);
    const container = startElement?.closest("[data-container-id]") || null;
    if (!container || !endElement || !container.contains(endElement)) {
      setShowHighlightBox(false);
      return;
    }

    const containerId = container?.getAttribute("data-container-id") || null;
  
    normalizeContainer(container); // Normalize before serialization
  
    const serialized = rangy.serializeRange(range, true, container);
    const className = `highlight-${color.replace("#", "")}`;
    highlighter.highlightSelection(className); // Apply color-specific highlight
  
    const highlightId = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    const highlight = {
      id: highlightId,
      color,
      className,
      letter,
      location: currentPath,
      containerId,
      serializedRange: serialized,
      excerpt: selection.toString(),
      savedIn: {
        user: letter === "I" || letter === "B",
        team: letter === "T" || letter === "B"
      }
    };
    
  
    addHighlight(highlight, getStorageTarget(letter));
    setShowHighlightBox(false);
  
    const newlyHighlighted = document.querySelectorAll(
      `span.${className}:not([data-highlight-id])`
    );

    console.log("[DEBUG] Newly highlighted spans:", newlyHighlighted);

    newlyHighlighted.forEach((span) => {
      span.setAttribute("data-highlight-id", highlightId);
    });
  };
  
  
   
  
  
  
  
  const reapplyHighlights = useCallback(() => {
    if (!highlighter) return;
  
    const containers = document.querySelectorAll("[data-container-id]");
    containers.forEach(container => {
      // Remove ALL previous highlights
      container.querySelectorAll("span.custom-highlight, span[class^='highlight-']").forEach((span) => {
        const parent = span.parentNode;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
      });
  
      // Clean up empty formatting tags
      container.querySelectorAll("strong, em, b").forEach((tag) => {
        if (!tag.textContent.trim()) tag.remove();
      });
  
      normalizeContainer(container);
    });
  
    highlighter.removeAllHighlights();
  
    highlights
    .filter(h => h.location === currentPath)
    .forEach(h => {
        try {
          const container = document.querySelector(`[data-container-id="${h.containerId}"]`);
          if (!container) return;
  
          normalizeContainer(container);
  
          let range;
          try {
            range = rangy.deserializeRange(h.serializedRange, container);
          } catch (e) {
            console.warn("Failed to deserialize range:", h, e);
            return;
          }

          if (!range || !range.startContainer || !container.contains(range.startContainer)) {
            console.warn("Skipped invalid range after deserialization:", h);
            return;
          }
        
  
          const highlighted = highlighter.highlightRanges(h.className || "custom-highlight", [range]);

          highlighted.forEach((hl) => {
            hl.getHighlightElements().forEach((el) => {
              el.setAttribute("data-highlight-id", h.id);
          
              // NEW — Tag the span with the saved source
              let source = "user";
              if (h.savedIn?.user && h.savedIn?.team) {
                source = "both";
              } else if (h.savedIn?.team) {
                source = "team";
              } else if (h.savedIn?.user) {
                source = "user";
              }
              el.setAttribute("data-source", source);
            });
          });          

        } catch (e) {
          console.warn("Failed to reapply highlight:", h, e);
        }
      });
  }, [currentPath, highlighter, highlights]);
  
  
  
  

  useEffect(() => {
    const waitAndReapply = () => {
      if (!highlighter) return;
  
      const checkReady = () => {
        // Only reapply if containers are loaded and highlights are non-empty
        const containersReady = document.querySelectorAll("[data-container-id]").length > 0;
        const hasHighlights = highlights && highlights.length > 0;
  
        if (containersReady && hasHighlights) {
          console.log("[DEBUG] Containers ready and highlights available. Reapplying...");
          reapplyHighlights();
        } else {
          console.log("[DEBUG] Containers not ready or no highlights yet. Retrying...");
          setTimeout(checkReady, 300); // Retry every 300ms until DOM and data are ready
        }
      };
  
      checkReady();
    };
  
    // Always clear old highlights when switching sources or path
    const containers = document.querySelectorAll("[data-container-id]");
    containers.forEach((container) => {
      const spans = container.querySelectorAll("span[style], span[class^='highlight-']");
      spans.forEach((span) => {
        const parent = span.parentNode;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
      });
      container.normalize();
    });

    if (highlighter) {
      highlighter.removeAllHighlights();
    }

    if (highlightSource === "none" || highlights.length === 0) {
      return; // Don't reapply if nothing should show
    }

    waitAndReapply(); // Now safely reapply the *new* source

  }, [highlights, highlightSource, currentPath, highlighter, reapplyHighlights]);
  

  
  
  
  

  useEffect(() => {
    const body = document.body;

    body.classList.remove("show-user", "show-team", "show-both");

    switch (highlightSource) {
      case "user":
        body.classList.add("show-user");
        break;
      case "team":
        body.classList.add("show-team");
        break;
      case "both":
        body.classList.add("show-both");
        break;
      default:
        break;
    }
  }, [highlightSource]);
  
  const highlightOptions = [
  { hex: "#00FF00", cssVar: "var(--highlight-green)" },
  { hex: "#FF0000", cssVar: "var(--highlight-red)" },
  { hex: "#FFFF00", cssVar: "var(--highlight-yellow-strong)" },
  { hex: "#FFC0CB", cssVar: "var(--highlight-pink)" },
  { hex: "#ADD8E6", cssVar: "var(--highlight-blue)" },
  { hex: "#FFA500", cssVar: "var(--highlight-orange)" }
];

  return (
    <>
      {showHighlightBox && (
        <div
          className="highlight-box"
          style={{
            top: `${highlightBoxPosition.top}px`,
            left: `${highlightBoxPosition.left}px`,
          }}
        >
          {/* Square letter box element */}
          <button
            className="highlight-letter-box"
            data-tooltip={tooltipTextMap[letter]}
            onClick={handleLetterClick}
          >
            {letter}
          </button>

  
          {highlightOptions.map(({ hex, cssVar }) => (
            <button
              key={hex}
              className="highlight-circle"
              style={{ backgroundColor: cssVar }}
              onClick={() => handleHighlight(hex)}
            />
          ))}
        </div>
      )}
  
      {/* ✅ This should NOT be nested inside showHighlightBox */}
      {selectedHighlightMeta && (
        <div
          ref={metaBoxRef}
          className="highlight-meta-box"
          style={{
            top: `${selectedHighlightMeta.position.top}px`,
            left: `${selectedHighlightMeta.position.left}px`,
          }}
        >
          <button
            className="highlight-meta-close app-cancel-control"
            onClick={() => setSelectedHighlightMeta(null)}
            aria-label="Close"
          >
            &times;
          </button>
          <p className="highlight-meta-title"><strong>Saved In:</strong></p>
          <ul className="highlight-meta-list">
            <li>User: {selectedHighlightMeta.savedIn?.user ? "✅" : "❌"}</li>
            <li>Team: {selectedHighlightMeta.savedIn?.team ? "✅" : "❌"}</li>
          </ul>
          {selectedHighlightMeta.savedIn?.user && (
            <button className="highlight-meta-button" onClick={() => {
              removeHighlight(selectedHighlightMeta, "user");
              setSelectedHighlightMeta(null);
            }}>Remove from User</button>
          )}
          {selectedHighlightMeta.savedIn?.team && (
            <button className="highlight-meta-button" onClick={() => {
              removeHighlight(selectedHighlightMeta, "team");
              setSelectedHighlightMeta(null);
            }}>Remove from Team</button>
          )}
          {(selectedHighlightMeta.savedIn?.user || selectedHighlightMeta.savedIn?.team) && (
            <button className="highlight-meta-button" onClick={() => {
              removeHighlight(selectedHighlightMeta, "both");
              setSelectedHighlightMeta(null);
            }}>Remove from Both</button>
          )}
        </div>
      )}
    </>
  );
   
};

export default Highlight;
